import React, { useEffect, useLayoutEffect, useRef, useState } from "react"
import { useNavigate, useParams } from 'react-router-dom'
import { pdfjs } from "react-pdf"
import { PDFDocument, rgb, StandardFonts } from "pdf-lib"
import { blobToURL } from "../../utils/Utils"
import { AddSigDialog } from "../../components/esign/AddSigDialog"
import { SignIntentDialog } from "../../components/esign/SignIntentDialog"
import ViewPdf from "../../components/ViewPdf/ViewPdf"
import { useDispatch } from "react-redux"
import { getSignDocRequest, postSignDocRequest } from "../../features/documents/documents"
import Button from "../../components/Button/Button"
import { showSnackBar } from "../../components/Snackbar/Snackbar"
import { useMyContextLoading } from "../../components/LoadingContextProvider/LoadingContextProvider"
import { getBaseUserByIdAnonymous } from "../../features/user/user"
import dayjs from "dayjs"
import timezone from "dayjs/plugin/timezone"
import utc from "dayjs/plugin/utc"
import { userTimeZone } from "../../utils/date"
import { getDocAccessId } from "../../utils/linkToken"
import usePinnedFeatureFlag from "../../hooks/usePinnedFeatureFlag"
import { ESIGN_LSA_SIGN_INTENT, ESIGN_LINK_PAGE_FIT_SCREEN } from "../../config/featureFlagKeys"
import { ESIGN_CONSENT, esignConsentText } from "../../config/esignConsent"
import ExpiredLink, { EXPIRED_LINK_MESSAGE } from "../BankAccount/ExpiredLink"

const SIGNING_REFUSAL_CODES = ['staffSessionSigning', 'signingInProgress', 'signingAuthRequired', 'signingIntentInvalid']
// WS 2312369: the API refuses a pending document last sent more than 30 days ago (ESIGN_PENDING_LINK_EXPIRY).
const LINK_EXPIRED_CODE = 'linkExpired'
const DOCUMENT_WIDTH = 612

dayjs.extend(utc)
dayjs.extend(timezone)

pdfjs.GlobalWorkerOptions.workerSrc = `${window.location.origin}/pdf.worker.js`

const EsignDoc = () => {
  const { accessUUID: accessUUIDFromPath } = useParams()
  // The fragment is the live form; the path parameter only still answers for a link that was
  // already in a client's inbox and whose lift into the fragment could not run.
  const accessUUID = getDocAccessId() || accessUUIDFromPath
  const { triggerLoading } = useMyContextLoading()
  const dispatch = useDispatch()
  const navigate = useNavigate()

  const [pdf, setPdf] = useState('')
  const [loadError, setLoadError] = useState(false)
  const [linkExpired, setLinkExpired] = useState(null)
  const [startBase64, setStartBase64] = useState('')
  const [signatureURL, setSignatureURL] = useState(false)
  const [signatureDialogVisible, setSignatureDialogVisible] = useState(false)
  const [pageNum, setPageNum] = useState(0)
  const [totalPages, setTotalPages] = useState(0)
  const [arraySigns, setArraySigns] = useState([])
  const [user, setUser] = useState({})
  const [pageDetails, setPageDetails] = useState(null)
  const [resetPage, setResetPage] = useState(null)
  const documentRef = useRef(null)
  const [disableSign, setDisableSign] = useState(false)
  const [typeDoc, setTypeDoc] = useState(null)

  const { enabled: intentFlag, pin: pinIntentFlag } = usePinnedFeatureFlag(ESIGN_LSA_SIGN_INTENT)
  const [serverRequiresIntent, setServerRequiresIntent] = useState(false)
  const intentMode = intentFlag || serverRequiresIntent
  const SignatureDialog = intentMode ? SignIntentDialog : AddSigDialog
  const [intent, setIntent] = useState(null)
  const firmBrandRef = useRef(null)

  const { enabled: fitScreen, pin: pinFitScreen } = usePinnedFeatureFlag(ESIGN_LINK_PAGE_FIT_SCREEN)
  const pageBoxRef = useRef(null)
  const [pageBoxWidth, setPageBoxWidth] = useState(0)

  useLayoutEffect(() => {
    if (!fitScreen) return undefined
    const box = pageBoxRef.current
    const measure = () => setPageBoxWidth(box?.clientWidth || 0)
    measure()
    if (typeof ResizeObserver === 'undefined') {
      window.addEventListener('resize', measure)
      return () => window.removeEventListener('resize', measure)
    }
    const observer = new ResizeObserver(measure)
    if (box) observer.observe(box)
    return () => observer.disconnect()
  }, [fitScreen])

  const goToSuccess = () => navigate(
    '/success/signing',
    firmBrandRef.current ? { replace: true, state: { firmBrand: firmBrandRef.current } } : { replace: true },
  )

  const styles = { documentBlock: { maxWidth: DOCUMENT_WIDTH, margin: "0 auto" } }

  const extractSignatureCoordinates = async (pdfFile) => {
    const loadingTask = pdfjs.getDocument(pdfFile)
    const pdfDoc = await loadingTask.promise
    const totalPages = pdfDoc.numPages
    const signatureCoordinates = []

    for (let i = 1; i <= totalPages; i++) {
      const page = await pdfDoc.getPage(i)
      const textContent = await page.getTextContent()

      textContent.items.forEach((item) => {
        const sigIndex = item.str.indexOf("{account_signature}")
        if (sigIndex !== -1) {
          const charWidth = item.width / item.str.length
          const offsetX = sigIndex * charWidth
          signatureCoordinates.push({
            page: i,
            x: item.transform[4] + offsetX - 2,
            y: item.transform[5],
            width: "{account_signature}".length * charWidth,
            type: "signature",
          })
        }

        const dateIndex = item.str.indexOf("{date_signed}")
        if (dateIndex !== -1) {
          const charWidth = item.width / item.str.length
          const offsetX = dateIndex * charWidth
          signatureCoordinates.push({
            page: i,
            x: item.transform[4] + offsetX,
            y: item.transform[5],
            width: "{date_signed}".length * charWidth,
            type: "date",
          })
        }
      })
    }

    return signatureCoordinates
  }

  const maskTemplateVariables = async (pdfFile) => {
    const coords = await extractSignatureCoordinates(pdfFile)
    if (!coords.length) return pdfFile

    const pdfDoc = await PDFDocument.load(pdfFile)
    const pages = pdfDoc.getPages()

    for (const item of coords) {
      const page = pages[item.page - 1]
      if (item.type === 'signature') {
        page.drawRectangle({
          x: item.x,
          y: item.y - 4,
          width: item.width,
          height: 13,
          color: rgb(1, 1, 1),
        })
      }
    }

    const pdfBytes = await pdfDoc.save()
    const blob = new Blob([new Uint8Array(pdfBytes)])
    return await blobToURL(blob)
  }

  const fillDateSigned = async (pdfFile) => {
    const coordinates = await extractSignatureCoordinates(pdfFile)
    const dateCoords = coordinates?.filter(c => c.type === 'date')
    if (!dateCoords.length) return pdfFile

    const pdfDoc = await PDFDocument.load(pdfFile)
    const font = await pdfDoc.embedFont(StandardFonts.Helvetica)
    const pages = pdfDoc.getPages()
    const today = dayjs().tz(userTimeZone).format('YYYY-MM-DD')

    for (const item of dateCoords) {
      const page = pages[item.page - 1]

      page.drawRectangle({
        x: item.x,
        y: item.y - 4,
        width: item.width + 5,
        height: 13,
        color: rgb(1, 1, 1),
      })

      page.drawText(today, {
        x: item.x,
        y: item.y,
        size: 11,
        font,
        color: rgb(0, 0, 0),
      })
    }

    const pdfBytes = await pdfDoc.save()
    const blob = new Blob([new Uint8Array(pdfBytes)])
    return await blobToURL(blob)
  }

  const getSignDoc = async () => {
    try {
      triggerLoading(true)
      setLoadError(false)
      const response = await dispatch(getSignDocRequest({ accessUUID }))
      if (response?.error && response?.payload?.code === LINK_EXPIRED_CODE) {
        setLinkExpired(response.payload.message || EXPIRED_LINK_MESSAGE)
        return
      }
      const data = response?.payload?.data
      firmBrandRef.current = data?.data?.firmBrand || null

      if (data?.data?.signed === true) {
        goToSuccess()
        return
      }

      if (!data?.pdf) {
        setLoadError(true)
        showSnackBar("error", "Unable to load the document. Please try again later or contact support.")
        return
      }

      setTypeDoc(data?.data?.typeDoc)
      // WS #2228906. A Card Payment Authorization is signed by the holder of someone else's card. The
      // client is loaded only to offer "fill with my name", which must not offer the client's name.
      if (data?.data?.typeDoc !== 'cardAuthorization') {
        const result = await dispatch(getBaseUserByIdAnonymous(data?.data?.user))
        setUser(result?.payload?.data)
      }

      const datedPdf = await fillDateSigned(data?.pdf)
      const maskedPdf = await maskTemplateVariables(datedPdf)

      setPdf(maskedPdf)
      setStartBase64(maskedPdf)
    } catch (error) {
      setLoadError(true)
      showSnackBar("error", "Unable to load the document. Please try again later or contact support.")
    } finally {
      triggerLoading(false)
    }
  }


  const onSetSignature = async (coordinates, signatureURL) => {
    const { originalWidth, originalHeight } = pageDetails
    const scale = originalWidth / (fitScreen ? DOCUMENT_WIDTH : documentRef.current.clientWidth)
    const pdfDoc = await PDFDocument.load(pdf)
    const pages = pdfDoc.getPages()

    for (const item of coordinates) {
      if (item.type !== 'signature') continue

      const page = pages[item.page - 1]
      const pngImage = await pdfDoc.embedPng(signatureURL)
      const pngDims = pngImage.scale(scale * 0.3)

      setResetPage(item.page - 1)
      setPageNum(item.page - 1)

      const signatureX = item.x
      const signatureY = item.y - (pngDims.height / 2)

      page.drawRectangle({
        x: item.x,
        y: item.y - 4,
        width: Math.max(item.width + 10, 160),
        height: 18,
        color: rgb(1, 1, 1),
      })

      page.drawImage(pngImage, {
        x: signatureX,
        y: signatureY,
        width: pngDims.width,
        height: pngDims.height,
      })
    }

    const pdfBytes = await pdfDoc.save()
    const blob = new Blob([new Uint8Array(pdfBytes)])
    const URL = await blobToURL(blob)

    const uint8Array = new Uint8Array(pdfBytes)
    const binaryString = Array.from(uint8Array, byte => String.fromCharCode(byte)).join('')
    const pdfBase64 = btoa(binaryString)
    const dataBase64 = 'data:application/pdf;base64,' + pdfBase64

    setArraySigns([{
      signedPdfBase64: dataBase64,
    }])

    setPdf(URL)
    setSignatureURL(null)
  }


  const onSigningAuthRequired = () => {
    if (intent) return
    setServerRequiresIntent(true)
    setPdf(startBase64)
    setArraySigns([])
    setSignatureDialogVisible(true)
  }

  const sign = async () => {
    try {
      triggerLoading(true)

      if (!arraySigns[0]?.signedPdfBase64) {
        showSnackBar("error", "Please add a signature before sending")
        return
      }

      if (startBase64 === pdf) {
        showSnackBar("error", "No changes were made to the document")
      } else {
        const response = await dispatch(postSignDocRequest({
          signedPdfBase64: arraySigns[0]?.signedPdfBase64,
          accessUUID,
          ...(intentMode ? { typedName: intent?.typedName, consent: intent?.consent } : {}),
        }))
        const payload = response?.payload

        if (response?.error) {
          if (payload?.code === 'alreadySigned') {
            goToSuccess()
            return
          }
          if (payload?.code === 'signingAuthRequired') {
            onSigningAuthRequired()
            return
          }
          if (payload?.code === LINK_EXPIRED_CODE) {
            setLinkExpired(payload.message || EXPIRED_LINK_MESSAGE)
            return
          }
          const message = SIGNING_REFUSAL_CODES.includes(payload?.code) ? payload.message : null
          showSnackBar("error", message || "An error occurred while signing the document")
          return
        }

        const data = payload?.data || payload

        if (data?.key?.length > 0) {
          showSnackBar("success", "The document was successfully signed")
          goToSuccess()
        } else {
          showSnackBar("error", "An error occurred while signing the document")
        }
      }
    } catch (error) {
      showSnackBar("error", "An error occurred while signing the document. Please try again.")
    } finally {
      triggerLoading(false)
    }
  }

  useEffect(() => {
    getSignDoc()
  }, [])

  if (linkExpired) {
    return <ExpiredLink message={linkExpired} testId="esign-link-expired" />
  }

  return (
    <div className={fitScreen ? "flex flex-col-reverse lg:flex-row max-w-[1360px] m-auto gap-6 px-4 lg:px-0" : "flex max-w-[1360px] m-auto gap-6"}>
      <div className={fitScreen ? "w-full lg:w-[438px]" : "w-[438px]"}>
        <div className="user_account_updateuserform flex flex-col items-start gap-6 p-6 rounded-2xl bg-paper border border-divider">
          {!signatureURL && (
            <div
              className="h-full w-full min-h-[56px] max-w-[390px] flex items-center cursor-pointer gap-2 rounded-lg border border-divider"
              onClick={() => {
                pinIntentFlag()
                pinFitScreen()
                setSignatureDialogVisible(true)
              }}>
              <div className="w-8 h-8 flex items-center justify-center ml-[14px]">
                <svg width={20} height={18} viewBox="0 0 20 18" fill="none"
                  xmlns="http://www.w3.org/2000/svg">
                  <path d="..." fill="black" />
                </svg>
              </div>
              <div className="text-text-primary">Add signature</div>
            </div>
          )}
          <div className="flex justify-end items-center gap-4 self-stretch py-6 px-0">
            <Button hover="hover:bg-primary-main/90" size="lg" width="w-[142px]" disabled={disableSign}
              onClick={() => {
                pinIntentFlag()
                pinFitScreen()
                sign()
              }}>
              Send
            </Button>
          </div>
        </div>
      </div>
      <div className="bg-surface w-full">
        <div ref={pageBoxRef} className="max-w-[612px] mx-auto">
          {signatureDialogVisible && (
            <SignatureDialog
              user={user}
              consentText={esignConsentText(typeDoc)}
              consentVersion={ESIGN_CONSENT.version}
              onClose={() => setSignatureDialogVisible(false)}
              onConfirm={async (result) => {
                setSignatureDialogVisible(false)
                if (intentMode) setIntent({ typedName: result.typedName, consent: result.consent })
                const url = intentMode ? result.signatureURL : result
                try {
                  const coordinates = await extractSignatureCoordinates(pdf)
                  if (coordinates.length > 0) {
                    await onSetSignature(coordinates, url)
                  } else {
                    showSnackBar("error", "No signature placeholders found in the document.")
                  }
                } catch (e) {
                  showSnackBar("error", "Could not insert signature.")
                }
              }}
            />
          )}
          {loadError && !pdf && (
            <div
              role="alert"
              className="flex flex-col items-center justify-center gap-2 py-16 px-6 text-center rounded-2xl border border-divider bg-paper">
              <div className="text-text-primary font-medium">This document could not be loaded</div>
              <div className="text-text-secondary text-sm">
                Please refresh the page and try again, or contact support if the problem persists.
              </div>
            </div>
          )}
          {pdf && (
            <div>
              <div ref={documentRef} style={styles.documentBlock}>
                <ViewPdf
                  sePageN={setPageNum}
                  totalPages={totalPages}
                  pdf={pdf}
                  width={fitScreen && pageBoxWidth > 0 && pageBoxWidth < DOCUMENT_WIDTH ? pageBoxWidth : undefined}
                  setups={true}
                  setTotalPages={setTotalPages}
                  setPageD={setPageDetails}
                  resetPage={resetPage}
                  onLoadError={() => setLoadError(true)}
                />
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  )
}

export default EsignDoc
